import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'
import { supabase } from '../lib/supabase'

export interface CriticalPowerPoint {
  duration: number // in seconds
  power: number // in watts
  label: string // e.g., "5s", "1min", "5min", "20min", "60min"
  isKey: boolean // whether this is a key metric to display in StatCards
  description?: string // for key metrics, provide a description
}

export interface CPModelEstimate {
  cp: number
  wPrime: number
  r2: number
  durations: number[]
}

export interface CPReference {
  dateRange: string[]
  values: CriticalPowerPoint[]
}

export interface WBalPoint {
  time: number
  wBal: number
  power: number
}

interface PowerStats {
  idleTime: number
  average: number
  stdDev: number
  min: number
  max: number
  sampleCount: number
}

interface UsePowerStatsReturn {
  stats: PowerStats | null
  loading: boolean
  error: string | null
  refreshStats: () => Promise<void>
}

export const usePowerStats = (workoutId: string): UsePowerStatsReturn => {
  const powerStatsQuery = useQuery<PowerStats | null>({
    queryKey: ['power-stats', workoutId],
    enabled: Boolean(workoutId),
    queryFn: async () => {
      const { data: metrics, error: metricsError } = await supabase
        .from('workout_metrics')
        .select('values')
        .eq('workout_id', workoutId)
        .eq('metric_name', 'power')
        .single()

      if (metricsError) {
        if ((metricsError as any).code === 'PGRST116') {
          return null
        }
        throw new Error(metricsError.message)
      }

      if (!metrics || !metrics.values || metrics.values.length === 0) {
        return null
      }

      const values = metrics.values.filter((val: number | null) => val !== null) as number[]
      if (values.length === 0) {
        return null
      }

      const n = values.length
      const mean = values.reduce((sum, hr) => sum + hr, 0) / n
      const squaredDiffs = values.map((hr) => (hr - mean) ** 2)
      const variance = squaredDiffs.reduce((sum, diff) => sum + diff, 0) / n
      const stdDev = Math.sqrt(variance)
      const min = Math.min(...values)
      const max = Math.max(...values)
      const idleTime = values.reduce((sum, power) => (power < 25 ? sum + 1 : sum), 0)

      return {
        idleTime,
        average: mean,
        stdDev: stdDev,
        min: min,
        max: max,
        sampleCount: n,
      }
    },
  })

  const refreshStats = useCallback(async () => {
    await powerStatsQuery.refetch()
  }, [powerStatsQuery])

  return {
    stats: powerStatsQuery.data ?? null,
    loading: powerStatsQuery.isLoading || powerStatsQuery.isFetching,
    error: powerStatsQuery.error ? (powerStatsQuery.error as Error).message : null,
    refreshStats,
  }
}
